import {
  FEATURES,
  GROUP_TYPE,
  IBroadcastInfo,
  IBroadcastInfoMembers,
  IGroupInfo,
  IGroupInfoMembers,
  IUserInfo,
} from "@home-health-notify/domain";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ListItemAvatar,
  Typography,
} from "@mui/material";
import { useContext } from "react";
import DisplayAvatarImage from "../../../common/DisplayAvatarImage";
import { RightDrawerContext } from "../../../context/RightDrawerContext";
import { RemoveMember } from "./RemoveMember";
import { OrganizationContext } from "hhn-commons";
import { MemberListItem, MemberListItemButton } from "./Members.style";

type MembersByTypeProps = {
  types: string[];
  header: string;
  allMembers: IGroupInfoMembers[] | IBroadcastInfoMembers[];
  userDirectory: IUserInfo[];
  goToChat: (userInfo: IUserInfo) => void;
  groupInfo: IGroupInfo | IBroadcastInfo;
};

function MembersByType({
  types,
  header,
  allMembers,
  userDirectory,
  goToChat,
  groupInfo,
}: MembersByTypeProps) {
  // To get the users number of each type
  const { closeDrawer } = useContext(RightDrawerContext);
  const { selectedOrgUser } = useContext(OrganizationContext);
  const hasRemoveMemberAccess = selectedOrgUser?.hasAccess(
    FEATURES.REMOVE_MEMBERS,
  );
  const isBroadcastGroup = groupInfo.groupType === GROUP_TYPE.BROADCAST;
  const isMember = groupInfo.isMember;
  const canRemoveMembers =
    hasRemoveMemberAccess && (isMember || isBroadcastGroup);

  let usersCount = 0;

  allMembers.forEach(member => {
    const userInfo = userDirectory.find(
      user => user.id === member.userId && types.includes(user.type),
    );
    if (userInfo) {
      usersCount++;
    }
  });

  // Global condition to check if there are users to display
  const shouldDisplayUsers = usersCount !== 0;
  const openChat = (userInfo: IUserInfo) => {
    goToChat(userInfo);
    closeDrawer();
  };
  return (
    <>
      {shouldDisplayUsers ? (
        <Accordion
          defaultExpanded={true}
          elevation={2}
          sx={{ borderRadius: "4px", background: "inherit" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="accordion-content"
          >
            <Typography data-testid="members-header-type">
              {header} ({usersCount})
            </Typography>
          </AccordionSummary>

          <AccordionDetails data-testid="filter-box">
            {allMembers.map(
              (member: IGroupInfoMembers | IBroadcastInfoMembers) => {
                const userInfo = userDirectory.find(
                  userInfo =>
                    userInfo.id === member.userId &&
                    types.includes(userInfo.type),
                );
                return (
                  <Box
                    sx={{ maxHeight: "70dvh", overflow: "auto" }}
                    key={member.userId}
                  >
                    {userInfo && (
                      <MemberListItem
                        key={member.userId}
                        disablePadding
                        secondaryAction={
                          canRemoveMembers && (
                            <RemoveMember
                              userInfo={userInfo}
                              groupInfo={groupInfo}
                            />
                          )
                        }
                      >
                        <MemberListItemButton
                          data-testid="group-member"
                          onClick={() => openChat(userInfo)}
                        >
                          <Box sx={{
                            flexDirection: "column"
                          }}>
                            <ListItemAvatar sx={{ marginLeft: "10px" }}>
                              <DisplayAvatarImage
                                src={userInfo.image}
                                alt={userInfo.name}
                              />
                            </ListItemAvatar>
                          </Box>
                          <Box>
                            <Typography>
                              {userInfo.title && `${userInfo.title}`}
                            </Typography>
                          </Box>
                        </MemberListItemButton>
                      </MemberListItem>
                    )}
                  </Box>
                );
              },
            )}
          </AccordionDetails>
        </Accordion>
      ) : (
        <Typography variant="body2"></Typography>
      )}
    </>
  );
}

export default MembersByType;
